import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Skeleton} from '@/components/ui/skeleton'
import {wompiMinimumProblem} from '@/components/cartera/amount-iva-fields'
import {DescuentoAtajo, ProcedenciaNote, SegmentedToggle} from '@/components/cartera/price-form-controls'
import {ScopedPriceTable} from '@/components/cartera/scoped-price-table'
import {SectionCard} from '@/components/section-card'
import {
  ALL_SEGMENT_KEYS,
  useResolverPrecio,
  useSetPriceOverride,
  type BillingCycle,
} from '@/hooks/inci/cartera/use-cartera'
import {publicarExito} from '@/lib/avisos'
import {translateApiError} from '@/lib/errors'
import {formatSegmentKey} from '@/lib/segments'

const PARTNER_GENERAL = '__general__'
const CYCLES: BillingCycle[] = ['mensual', 'anual']

function NewPriceForm({partnerId}: {partnerId: string}) {
  const {t} = useTranslation()
  const [segment, setSegment] = useState<string>(PARTNER_GENERAL)
  const [cycle, setCycle] = useState<BillingCycle>('mensual')
  const [amount, setAmount] = useState('')
  const [error, setError] = useState<string | null>(null)
  const setOverride = useSetPriceOverride()
  const segmentKey = segment === PARTNER_GENERAL ? null : segment

  const resolver = useResolverPrecio({segmentKey, billingCycle: cycle, partnerId, enabled: true})

  // No envía iva_pct: el backend aplica `PriceBookEntryCreate.iva_pct = 19`, y contra ese
  // valor se revisa el mínimo de Wompi.
  function amountProblem(amountStr: string): string | null {
    const amountNum = Number(amountStr)
    if (!amountStr.trim() || !Number.isFinite(amountNum) || amountNum < 0) {
      return t('cartera.precios.amountInvalid')
    }
    return wompiMinimumProblem(t, Math.round(amountNum), 19)
  }

  function submit() {
    const problem = amountProblem(amount)
    if (problem) {
      setError(problem)
      return
    }
    setError(null)
    setOverride.mutate(
      {
        item_kind: 'saas',
        segment_key: segmentKey,
        billing_cycle: cycle,
        amount_cop: Math.round(Number(amount)),
        partner_id: partnerId,
      },
      {
        onSuccess: () => {
          setAmount('')
          publicarExito(t('cartera.overrides.guardado'), t('cartera.overrides.guardadoDetalle'), {
            testId: 'partner-detail-precio-success',
          })
        },
        onError: (err) => setError(translateApiError(err)),
      }
    )
  }

  return (
    <div className="space-y-3" data-testid="partner-detail-precio-nuevo">
      <div className="flex flex-wrap items-end gap-3">
        <div className="max-w-xs flex-1 space-y-1.5">
          <Label htmlFor="partner-detail-segmento">{t('cartera.overrides.segmentoLabel')}</Label>
          <select
            id="partner-detail-segmento"
            data-testid="partner-detail-segmento"
            value={segment}
            onChange={(e) => setSegment(e.target.value)}
            className="h-9 w-full rounded-md border border-input bg-transparent px-2 text-sm text-foreground">
            <option value={PARTNER_GENERAL}>{t('cartera.overrides.segmentoGeneral')}</option>
            {ALL_SEGMENT_KEYS.map((key) => (
              <option key={key} value={key}>
                {formatSegmentKey(key, t)}
              </option>
            ))}
          </select>
        </div>
        <SegmentedToggle
          value={cycle}
          options={CYCLES}
          optionLabel={(c) => t(`cartera.precios.${c}`)}
          onChange={setCycle}
          testId="partner-detail-ciclo"
        />
      </div>

      {resolver.isLoading ? (
        <Skeleton className="h-4 w-48" />
      ) : (
        <ProcedenciaNote loading={false} resolution={resolver.data} testId="partner-detail-procedencia" />
      )}

      <DescuentoAtajo baseAmount={resolver.data?.amount_cop ?? null} onApply={(value) => setAmount(String(value))} />

      <div className="max-w-xs space-y-1.5">
        <Label htmlFor="partner-detail-precio-amount">{t('cartera.precios.amountLabel')}</Label>
        <Input
          id="partner-detail-precio-amount"
          data-testid="partner-detail-precio-amount"
          type="number"
          min={0}
          value={amount}
          onChange={(e) => {
            setAmount(e.target.value)
            setError(null)
          }}
          onBlur={() => setError(amount.trim() === '' ? null : amountProblem(amount))}
        />
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}

      <Button
        type="button"
        disabled={setOverride.isPending}
        data-testid="partner-detail-precio-submit"
        onClick={submit}>
        {setOverride.isPending ? t('common.saving') : t('cartera.overrides.submit')}
      </Button>
    </div>
  )
}

export function PreciosSection({partnerId}: {partnerId: string}) {
  const {t} = useTranslation()
  return (
    <SectionCard title={t('partnerDetail.billing.precios')} bodyClassName="space-y-4 px-5 py-4">
      <ScopedPriceTable scope={{partnerId}} testPrefix="partner-detail-price" />
      <div className="surface-divider border-t pt-4">
        <NewPriceForm partnerId={partnerId} />
      </div>
    </SectionCard>
  )
}
